import { Box, Button, Card, CardBody, CardFooter, CardHeader, Icon, Input, InputGroup, InputLeftElement, InputRightElement } from '@chakra-ui/react';
import React, { FC, useCallback, useState } from 'react';
import { FaCircleUser, FaEyeSlash, FaEye } from 'react-icons/fa6';
import { PiPasswordBold } from 'react-icons/pi';

import { eventValue } from '@app/utils';
import { useAlert } from '@app/hooks/use-alert';
import { useApi } from '@app/hooks';

import Logo from '../common/icons/logo';
import { SingInPayload } from '../../api/auth';
import { Txt } from '../common';

interface LoginProps {
  onLogin?: () => void;
}

interface ValidationFlags {
  usernameIsEmpty?: boolean;
  passwordIsEmpty?: boolean;
}

const Login: FC<LoginProps> = (props) => {
  const { onLogin } = props;
  const { error, warning } = useAlert();
  const [credentials, setCredentials] = useState<SingInPayload>();
  const [validationFlags, setValidationFlags] = useState<ValidationFlags>({});
  const api = useApi();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const onShowPasswordClick = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  const onChangeCredentials = useCallback((field: keyof SingInPayload) => (value: string) => {
    setCredentials({
      ...credentials,
      [field]: value
    });
  }, [credentials]);

  const onFormSubmit = async () => {
    if (!credentials) {
      warning('Введите имя пользователя и пароль');
      return;
    }
    
    if (credentials.password === '' || credentials.username === '') {
      setValidationFlags({
        usernameIsEmpty: credentials.username === '',
        passwordIsEmpty: credentials.password === '',
      });
      warning('Введите имя пользователя и пароль');
      return;
    }
    setLoading(true);
    try {
      await api.auth.singIn(credentials);
      onLogin && onLogin();
    } catch (e) {
      console.log(e);
      error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const onKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      onFormSubmit();
    }
  };

  return (
    <Box alignSelf="center" marginX="auto">
      <Card overflow="hidden" width={{ md: 400 }}>
        <CardHeader  backgroundImage='url(/images/bkg.png)' padding={0} display='flex' alignItems='center'>
          <Logo variant="horizontal" />
          <Txt ml={-3} fontFamily='monospace' mt={1} color='brand.100' >.web</Txt>
        </CardHeader>
        <CardBody display='flex' flexDirection='column' gap={4}>
          <InputGroup>
            <InputLeftElement pointerEvents='none'>
              <Icon scale={2} as={FaCircleUser} color='brand.300' />
            </InputLeftElement>
            <Input 
              type='text' 
              isInvalid={validationFlags.usernameIsEmpty}
              placeholder='Имя пользователя' 
              onKeyPress={onKeyPress}
              onChange={eventValue(onChangeCredentials('username'))}
            />
          </InputGroup>
          <InputGroup>
            <InputLeftElement pointerEvents='none'>
              <Icon as={PiPasswordBold} color='brand.300' />
            </InputLeftElement>
            <Input 
              type={showPassword ? 'text' : 'password'} 
              isInvalid={validationFlags.passwordIsEmpty}
              placeholder='Пароль' 
              onKeyPress={onKeyPress} 
              onChange={eventValue(onChangeCredentials('password'))}
            />
            <InputRightElement onClick={onShowPasswordClick} cursor="pointer">
              {!showPassword && <Icon as={FaEye} color='gray.400' />}
              {showPassword && <Icon as={FaEyeSlash} color='gray.400' />}
            </InputRightElement>
          </InputGroup>
        </CardBody>
        <CardFooter justifyContent="center" >
          <Button onClick={onFormSubmit} isLoading={loading} variant='solid' colorScheme="brand">Войти</Button>
        </CardFooter>
      </Card>
    </Box>
  );
};

export default Login;